// public/js/features/files/api.js
// Thin wrappers around the asset-files REST endpoints plus the three-step upload
// flow (presign -> direct S3 PUT -> metadata confirm).
//
// Why the detour via S3: API Gateway caps request bodies at ~10 MB and base64
// inflates them further, so the bytes must never pass through the Lambda. The
// Lambda only validates + signs, the browser PUTs straight to the bucket.
// See docs/asset-files.md §1.
import { renderPdfThumb, isPdfType } from './pdf-thumb.js';

// ── limits + type whitelist (mirrors config.files in code/config.mjs) ─────────

export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export const ALLOWED_TYPES = [
  'application/pdf',
  'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/heic', 'image/heif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword', 'application/vnd.ms-excel',
  'text/plain', 'text/csv', 'application/json',
];

// Browsers disagree about (or omit) the MIME type of files picked from disk —
// .heic and .csv are routinely handed over as '' or a vendor type. Fall back to
// the extension so a legitimate file is not rejected before it is even sent.
const EXT_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp',
  gif: 'image/gif', heic: 'image/heic', heif: 'image/heif',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword', xls: 'application/vnd.ms-excel',
  txt: 'text/plain', csv: 'text/csv', json: 'application/json', log: 'text/plain',
};

// Image derivative sizing (docs/asset-files.md §6).
const WEB_MAX_EDGE = 2000;
const THUMB_MAX_EDGE = 320;
const JPEG_QUALITY = 0.85;

export function fileExtension(name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ''));
  return m ? m[1].toLowerCase() : '';
}

export function stripExtension(name) {
  return String(name || '').replace(/\.[^.]+$/, '') || String(name || '');
}

/** Best-effort content type for a File: the browser's, else the extension's. */
export function resolveContentType(file) {
  const declared = String(file?.type || '').toLowerCase();
  if (ALLOWED_TYPES.includes(declared)) return declared;
  const byExt = EXT_TYPES[fileExtension(file?.name)];
  return byExt || declared || 'application/octet-stream';
}

/** Client-side gate mirroring the server's. → { ok, error }. */
export function validateFile(file, contentType = resolveContentType(file)) {
  if (!file || !file.size) return { ok: false, error: 'The file is empty.' };
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, error: `Too large (${Math.round(file.size / 1048576)} MB). The limit is ${MAX_FILE_BYTES / 1048576} MB.` };
  }
  if (!ALLOWED_TYPES.includes(contentType)) {
    return { ok: false, error: `Unsupported file type${contentType ? ` (${contentType})` : ''}.` };
  }
  return { ok: true };
}

// ── REST wrappers ────────────────────────────────────────────────────────────

export async function getUploadUrl({ assetUUID, fileName, contentType, size, kind = 'file', variants = ['original'] }) {
  const res = await window.authenticatedFetch('api/v1/files/upload-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assetUUID, fileName, contentType, size, kind, variants }),
  });
  if (!res.ok) throw new Error((await safeErr(res)) || 'Could not prepare the upload');
  return await res.json(); // { fileId, keys, uploads }
}

export async function createFileRecord(payload) {
  const res = await window.authenticatedFetch('api/v1/files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await safeErr(res)) || 'Could not save the file');
  return await res.json(); // { file }
}

export async function listAssetFiles(assetUUID, { kind } = {}) {
  const qs = new URLSearchParams({ asset: assetUUID });
  if (kind) qs.set('kind', kind);
  const res = await window.authenticatedFetch(`api/v1/files?${qs}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Failed to load files');
  return (await res.json()).files || [];
}

export async function listTenantFiles(tenantId, { kind } = {}) {
  const qs = new URLSearchParams({ tenant: tenantId });
  if (kind) qs.set('kind', kind);
  const res = await window.authenticatedFetch(`api/v1/files?${qs}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Failed to load files');
  return (await res.json()).files || [];
}

export async function getFile(fileId) {
  const res = await window.authenticatedFetch(`api/v1/files/${encodeURIComponent(fileId)}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Failed to load the file');
  return (await res.json()).file || null;
}

/** Short-lived presigned GET URL for one variant (never a public object). */
export async function getFileUrl(fileId, variant = 'original') {
  const res = await window.authenticatedFetch(`api/v1/files/${encodeURIComponent(fileId)}/url?variant=${encodeURIComponent(variant)}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Failed to get the download link');
  return (await res.json()).url;
}

export async function updateFile(fileId, updates) {
  const res = await window.authenticatedFetch(`api/v1/files/${encodeURIComponent(fileId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error((await safeErr(res)) || 'Update failed');
  return await res.json();
}

export async function deleteFile(fileId) {
  const res = await window.authenticatedFetch(`api/v1/files/${encodeURIComponent(fileId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error((await safeErr(res)) || 'Delete failed');
  return await res.json();
}

export async function listDocTypes(tenantId) {
  const qs = tenantId ? `?tenant=${encodeURIComponent(tenantId)}` : '';
  const res = await window.authenticatedFetch(`api/v1/doc-types${qs}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Failed to load document types');
  return (await res.json()).docTypes || [];
}

/** Hybrid search: keyword always, `ask` adds the LLM answer over indexed texts. */
export async function searchFiles({ tenantId, q, ask = false, assetUUID = null, kind = null } = {}) {
  const qs = new URLSearchParams({ tenant: tenantId || '', q: q || '', ask: ask ? '1' : '0' });
  // Narrowing to one asset matters most for "Ask AI": on an asset page the
  // answer must come from THAT machine's documents, not the whole tenant's.
  if (assetUUID) qs.set('asset', assetUUID);
  if (kind) qs.set('kind', kind);
  const res = await window.authenticatedFetch(`api/v1/files/search?${qs}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Search failed');
  const data = await res.json();
  return { results: data.results || [], answer: data.answer || null };
}

// ── upload flow ──────────────────────────────────────────────────────────────

/**
 * PUT raw bytes to a presigned S3 URL.
 * XHR instead of fetch purely for `upload.progress` — honest per-file progress.
 * No headers are set: the signature covers `host` only, and anything we add by
 * hand (Content-Type above all) risks a mismatch for zero benefit.
 */
export function putBytes(url, blob, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload rejected by storage (HTTP ${xhr.status})`));
    });
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    xhr.send(blob);
  });
}

/**
 * Canvas-derived preview versions of an upload. The original bytes are always
 * kept untouched — evidence value and zoom.
 *
 * Images get a web copy plus a thumbnail; a PDF gets a thumbnail rendered from
 * page 1 (pdf.js, loaded on demand — see pdf-thumb.js). Everything else, and
 * anything the browser cannot decode (HEIC on most desktops, an encrypted PDF),
 * returns {} so the upload degrades to original-only instead of failing.
 */
export async function makeDerivatives(file, contentType = resolveContentType(file)) {
  if (isPdfType(contentType)) {
    const thumb = await renderPdfThumb(file, THUMB_MAX_EDGE);
    return thumb ? { thumb } : {};
  }
  if (!contentType.startsWith('image/')) return {};
  try {
    const source = await decodeImage(file);
    const out = {};
    // An animated GIF would be flattened to a still frame, so it keeps the
    // original as its display version and only gets a static thumbnail.
    if (contentType !== 'image/gif') {
      const web = await rescale(source, WEB_MAX_EDGE);
      if (web) out.web = web;
    }
    const thumb = await rescale(source, THUMB_MAX_EDGE);
    if (thumb) out.thumb = thumb;
    source.close?.();
    return out;
  } catch {
    return {};
  }
}

function decodeImage(file) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file);
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image decode failed')); };
    img.src = url;
  });
}

function rescale(source, maxEdge) {
  const w = source.width || source.naturalWidth;
  const h = source.height || source.naturalHeight;
  if (!w || !h) return Promise.resolve(null);
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  // Transparent PNGs would otherwise turn black once flattened into JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
}

/**
 * The complete upload: presign → PUT every variant → confirm the metadata row.
 * `onProgress(fraction)` is byte-weighted across all variants. Resolves with the
 * created file record.
 */
export async function uploadFile(file, { assetUUID, kind = 'file', label, docTypeId, contentType, onProgress } = {}) {
  if (!assetUUID) throw new Error('No asset selected for this upload');
  const type = contentType || resolveContentType(file);
  const check = validateFile(file, type);
  if (!check.ok) throw new Error(check.error);

  const parts = { original: file, ...(await makeDerivatives(file, type)) };
  const presign = await getUploadUrl({
    assetUUID, fileName: file.name, contentType: type, size: file.size,
    kind, variants: Object.keys(parts),
  });

  // Only upload what the server actually signed.
  const variants = Object.keys(parts).filter(v => presign.uploads?.[v]);
  if (!variants.includes('original')) throw new Error('Storage did not accept this file');

  const loaded = {};
  const total = variants.reduce((sum, v) => sum + (parts[v].size || 0), 0) || 1;
  const report = () => onProgress?.(Math.min(1, variants.reduce((s, v) => s + (loaded[v] || 0), 0) / total));

  for (const v of variants) {
    await putBytes(presign.uploads[v], parts[v], (bytes) => { loaded[v] = bytes; report(); });
    loaded[v] = parts[v].size || 0;
    report();
  }

  const created = await createFileRecord({
    fileId: presign.fileId,
    assetUUID,
    s3Key: presign.keys.original,
    webKey: variants.includes('web') ? presign.keys.web : null,
    thumbKey: variants.includes('thumb') ? presign.keys.thumb : null,
    fileName: file.name,
    contentType: type,
    size: file.size,
    label: (label || '').trim() || stripExtension(file.name),
    docTypeId: kind === 'photo' ? null : (docTypeId || 'other'),
    kind,
  });
  onProgress?.(1);
  return created.file || created;
}

async function safeErr(res) {
  try { return (await res.json()).error; } catch { return null; }
}

// ── Naming aliases ──────────────────────────────────────────────────────────
// The explorer/section modules were written against these names; keeping both
// spellings avoids a rename sweep across surfaces that call one or the other.
export const getFileDetail = (fileId) => getFile(fileId);
export const patchFile = (fileId, updates) => updateFile(fileId, updates);
export const searchTenantFiles = (tenantId, q, opts = {}) => searchFiles({ tenantId, q, ask: opts.ask });

// Front-photo thumbnails for a whole tenant in ONE call (assets list).
export async function getTenantThumbs(tenantId) {
  const res = await window.authenticatedFetch(`api/v1/files/thumbs?tenant=${encodeURIComponent(tenantId)}`);
  if (!res.ok) throw new Error((await safeErr(res)) || 'Failed to load thumbnails');
  return (await res.json()).thumbs || {};
}
